import {
    TrendingUp,
    TrendingDown,
    Wallet,
    Clock,
    Baby,
    Library,
    BookOpen,
    Briefcase,
} from 'lucide-react';
import type {
    FinancialStats,
    School,
    Transaction,
    BudgetItem,
    ChartDataPoint,
} from './types';

// Global Yayasan Financial Statistics
export const yayasanStats: FinancialStats[] = [
    {
        label: 'Total Pendapatan',
        value: 'Rp 1.571.900.000',
        change: '+12.5%',
        icon: TrendingUp,
    },
    {
        label: 'Total Pengeluaran',
        value: 'Rp 587.100.000',
        change: '+5.2%',
        icon: TrendingDown,
    },
    {
        label: 'Total Saldo Bersih',
        value: 'Rp 984.800.000',
        change: '+18.4%',
        icon: Wallet,
    },
    {
        label: 'Tagihan Pending',
        value: 'Rp 12.400.000',
        change: '-2.1%',
        icon: Clock,
    },
];

// All Schools Data (Aggregate Units only)
export const schools: School[] = [
    {
        id: 'paud',
        name: 'PAUD / TK Unit',
        income: '120 Jt',
        expense: '45 Jt',
        balance: '75 Jt',
        color: 'bg-primary-soft text-primary',
        icon: Baby
    },
    {
        id: 'sd',
        name: 'SD Dasar Unit',
        income: '451 Jt',
        expense: '180 Jt',
        balance: '270 Jt',
        color: 'bg-primary-soft text-primary',
        icon: Library,
    },
    {
        id: 'smp',
        name: 'SMP Menengah Unit',
        income: '380 Jt',
        expense: '151 Jt',
        balance: '229 Jt',
        color: 'bg-warning-soft text-warning',
        icon: BookOpen,
    },
    {
        id: 'smk',
        name: 'SMK Kejuruan Unit',
        income: '620 Jt',
        expense: '211 Jt',
        balance: '410 Jt',
        color: 'bg-info-soft text-info',
        icon: Briefcase,
    },
];

// Schools Data by Period
export const schoolsByPeriod = {
    daily: [
        {
            id: 'paud',
            name: 'PAUD / TK Unit',
            income: '4 Jt',
            expense: '1.5 Jt',
            balance: '2.5 Jt',
            color: 'bg-primary-soft text-primary',
            icon: Baby,
        },
        {
            id: 'sd',
            name: 'SD Dasar Unit',
            income: '15 Jt',
            expense: '6 Jt',
            balance: '9 Jt',
            color: 'bg-primary-soft text-primary',
            icon: Library,
        },
        {
            id: 'smp',
            name: 'SMP Menengah Unit',
            income: '13 Jt',
            expense: '5 Jt',
            balance: '8 Jt',
            color: 'bg-warning-soft text-warning',
            icon: BookOpen,
        },
        {
            id: 'smk',
            name: 'SMK Kejuruan Unit',
            income: '21 Jt',
            expense: '7 Jt',
            balance: '14 Jt',
            color: 'bg-info-soft text-info',
            icon: Briefcase,
        },
    ],
    weekly: [
        {
            id: 'paud',
            name: 'PAUD / TK Unit',
            income: '29 Jt',
            expense: '11 Jt',
            balance: '18 Jt',
            color: 'bg-primary-soft text-primary',
            icon: Baby,
        },
        {
            id: 'sd',
            name: 'SD Dasar Unit',
            income: '105 Jt',
            expense: '42 Jt',
            balance: '63 Jt',
            color: 'bg-primary-soft text-primary',
            icon: Library,
        },
        {
            id: 'smp',
            name: 'SMP Menengah Unit',
            income: '89 Jt',
            expense: '35 Jt',
            balance: '54 Jt',
            color: 'bg-warning-soft text-warning',
            icon: BookOpen,
        },
        {
            id: 'smk',
            name: 'SMK Kejuruan Unit',
            income: '145 Jt',
            expense: '49 Jt',
            balance: '96 Jt',
            color: 'bg-info-soft text-info',
            icon: Briefcase,
        },
    ],
    monthly: schools, // Default is monthly
    yearly: [
        {
            id: 'paud',
            name: 'PAUD / TK Unit',
            income: '1.44 M',
            expense: '540 Jt',
            balance: '900 Jt',
            color: 'bg-primary-soft text-primary',
            icon: Baby,
        },
        {
            id: 'sd',
            name: 'SD Dasar Unit',
            income: '5,4 M',
            expense: '2,2 M',
            balance: '3,2 M',
            color: 'bg-primary-soft text-primary',
            icon: Library,
        },
        {
            id: 'smp',
            name: 'SMP Menengah Unit',
            income: '4,6 M',
            expense: '1,8 M',
            balance: '2,8 M',
            color: 'bg-warning-soft text-warning',
            icon: BookOpen,
        },
        {
            id: 'smk',
            name: 'SMK Kejuruan Unit',
            income: '7,4 M',
            expense: '2,5 M',
            balance: '4,9 M',
            color: 'bg-info-soft text-info',
            icon: Briefcase,
        },
    ],
};


// Recent Transactions (Global)
export const recentTransactions: Transaction[] = [
    {
        id: 'TRX001',
        type: 'income',
        description: 'Pembayaran SPP Siswa Semester Ganjil',
        amount: 'Rp 45.500.000',
        date: '2025-02-01',
        category: 'SPP',
        school: 'SD Dasar Unit',
    },
    {
        id: 'TRX002',
        type: 'expense',
        description: 'Pembelian Alat Tulis Kantor',
        amount: 'Rp 2.300.000',
        date: '2025-02-01',
        category: 'Operasional',
        school: 'SMP Menengah Unit',
    },
    {
        id: 'TRX003',
        type: 'income',
        description: 'Uang Pangkal Siswa Baru',
        amount: 'Rp 18.000.000',
        date: '2025-01-31',
        category: 'Uang Pangkal',
        school: 'PAUD Al-Azhar Pekanbaru',
    },
    {
        id: 'TRX004',
        type: 'expense',
        description: 'Gaji Guru dan Staff Januari',
        amount: 'Rp 125.000.000',
        date: '2025-01-31',
        category: 'Gaji',
        school: 'SMK Kejuruan Unit',
    },
    {
        id: 'TRX005',
        type: 'income',
        description: 'Subsidi Pemerintah Triwulan 1',
        amount: 'Rp 75.000.000',
        date: '2025-01-30',
        category: 'Subsidi',
        school: 'SD Dasar Unit',
    },
];

// Budget Overview (Global)
export const budgetOverview: BudgetItem[] = [
    {
        category: 'Gaji & Tunjangan',
        allocated: 'Rp 450M',
        used: 'Rp 380M',
        percentage: 84,
        color: 'bg-primary',
    },
    {
        category: 'Operasional',
        allocated: 'Rp 150M',
        used: 'Rp 95M',
        percentage: 63,
        color: 'bg-success',
    },
    {
        category: 'Infrastruktur',
        allocated: 'Rp 200M',
        used: 'Rp 120M',
        percentage: 60,
        color: 'bg-warning',
    },
    {
        category: 'Pengembangan',
        allocated: 'Rp 100M',
        used: 'Rp 45M',
        percentage: 45,
        color: 'bg-info',
    },
];

// Financial Chart Data (Global - Last 6 Months)
export const financialChartData: ChartDataPoint[] = [
    { month: 'Jan', income: 275000000, expense: 105000000 },
    { month: 'Feb', income: 242000000, expense: 77000000 },
    { month: 'Mar', income: 260000000, expense: 95000000 },
    { month: 'Apr', income: 285000000, expense: 115000000 },
    { month: 'Mei', income: 270000000, expense: 100000000 },
    { month: 'Jun', income: 290000000, expense: 125000000 },
    { month: 'Jul', income: 310000000, expense: 140000000 },
    { month: 'Agu', income: 280000000, expense: 110000000 },
    { month: 'Sep', income: 245000000, expense: 98000000 },
    { month: 'Okt', income: 268000000, expense: 102000000 },
    { month: 'Nov', income: 252000000, expense: 95000000 },
    { month: 'Des', income: 320000000, expense: 180000000 },
];
